import { useAttributionFormatters } from "@/hooks/use-attribution-formatters"
import React from "react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Info,
  Plus,
  Search,
  Trash2,
} from "lucide-react"

import {
  ChannelStat,
  EditingExpenseState,
  ExpenseEditingRules,
  InfluencerActionPrompt,
  InfluencerRecord,
  MetadataBadgeContext,
  SourceDetail,
} from "./types"
import { formatCurrency } from "./utils"
import { isRoiPositive, isExpenseEmpty } from "./format-display"
import { DeltaBadge } from "@/components/ui/analytics/kpi-card"
import type { ChannelDeltas } from "./use-comparison-data"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




interface ExpenseCellProps {
  displayValue: number
  isEditing: boolean
  currentValue: number
  onChange: (value: number) => void
  onSave: () => void
  onCancel: () => void
  canEdit: boolean
  viewButtonDisabledReason?: string
  isMultiMonth: boolean
  onEnterEdit: () => void
  deleteButton?: {
    visible: boolean
    canDelete: boolean
    reason?: string
    onDelete: () => void
  }
}

const ExpenseCell = ({
  displayValue,
  isEditing,
  currentValue,
  onChange,
  onSave,
  onCancel,
  canEdit,
  viewButtonDisabledReason,
  isMultiMonth,
  onEnterEdit,
  deleteButton,
}: ExpenseCellProps) => {
  const { formatExpenseDisplay } = useAttributionFormatters()

  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardChannelTable")
  const tCommonUi = useTranslations("Common")

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          className="w-24 px-2 py-1 border rounded text-sm"
          value={Number.isNaN(currentValue) ? 0 : currentValue}
          onFocus={(event) => {
            if (event.target.value === "0") event.target.value = ""
          }}
          onBlur={(event) => {
            if (event.target.value === "") event.target.value = "0"
          }}
          onChange={(event) => onChange(Number.parseFloat(event.target.value) || 0)}
          disabled={!canEdit}
        />
        <Button size="sm" onClick={onSave} disabled={!canEdit}>
          <span key={tUi("save")}>{tUi("save")}</span></Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
      </div>
    )
  }

  const isEmpty = isExpenseEmpty(displayValue)
  const valueLabel = (
    <>
      <span className={isEmpty ? "text-muted-foreground" : ""}>
        {formatExpenseDisplay(displayValue)}
      </span>
      {isMultiMonth && !isEmpty && <span className="ml-1 text-xs text-muted-foreground"><span key={tUi("sum")}>{tUi("sum")}</span></span>}
    </>
  )

  const buttonContent = (
    <Button
      variant="ghost"
      size="sm"
      onClick={onEnterEdit}
      disabled={!canEdit}
      className={cn("text-sm", !canEdit && "cursor-not-allowed opacity-50")}
    >
      {valueLabel}
    </Button>
  )

  const maybeWrappedButton =
    canEdit || !viewButtonDisabledReason ? (
      buttonContent
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{viewButtonDisabledReason}</p>
        </TooltipContent>
      </Tooltip>
    )

  return (
    <div className="flex items-center gap-2">
      {maybeWrappedButton}
      {deleteButton?.visible && !isEmpty && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={deleteButton.onDelete}
              disabled={!deleteButton.canDelete}
              className={cn("h-8 w-8 p-0", !deleteButton.canDelete && "cursor-not-allowed opacity-50")}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </TooltipTrigger>
          {!deleteButton.canDelete && deleteButton.reason && (
            <TooltipContent>
              <p className="text-xs">{deleteButton.reason}</p>
            </TooltipContent>
          )}
        </Tooltip>
      )}
    </div>
  )
}

function InlineBar({ value, max, color, children }: {
  value: number; max: number; color: string; children: React.ReactNode
}) {
  return (
    <div className="relative">
      <div
        className="absolute inset-y-0 left-0 rounded-sm opacity-10"
        style={{ width: `${Math.min((value / max) * 100, 100)}%`, backgroundColor: color }}
      />
      <span className="relative">{children}</span>
    </div>
  )
}

interface ChannelPerformanceTableProps {
  stats: ChannelStat[]
  isLoading: boolean
  toggleSort: (column: "channel" | "orders" | "revenue" | "aov" | "roi") => void
  canEditChannelExpense: (
    channelSlug: string,
    opts?: { allowChannelLevelExpense?: boolean },
  ) => { canEdit: boolean; reason: string }
  canEditSourceExpense: (channelSlug: string, source: string) => { canEdit: boolean; reason: string }
  canDeleteExpense: (channelSlug: string, source?: string) => { canDelete: boolean; reason: string }
  shouldShowDelete: (channelSlug: string, source?: string) => boolean
  openDeleteConfirmation: (channelSlug: string, channelName: string, source?: string) => void
  editingExpense: EditingExpenseState | null
  setEditingExpense: (value: EditingExpenseState | null) => void
  expenseInputs: Record<string, number>
  setExpenseInputs: React.Dispatch<React.SetStateAction<Record<string, number>>>
  saveExpense: (channelSlug: string, channelName: string, source?: string) => void | Promise<void>
  expenseEditingRules: ExpenseEditingRules
  expandedChannels: Set<string>
  toggleChannel: (channelKey: string) => void
  sourceFilters: Record<string, string>
  updateSourceFilter: (channel: string, filterText: string) => void
  getFilteredSources: (channel: string, sources: SourceDetail[]) => SourceDetail[]
  expandedInfluencers: Set<string>
  toggleInfluencerRow: (key: string) => void
  influencers: InfluencerRecord[]
  openCreateInfluencerDialog: () => void
  openAddCodeDialog: (influencer: InfluencerRecord) => void
  setInfluencerActionPrompt: React.Dispatch<React.SetStateAction<InfluencerActionPrompt | null>>
  onMetadataBadgeClick: (context: MetadataBadgeContext) => void
  channelDeltas?: Map<string, ChannelDeltas> | null
  sourceDeltas?: Map<string, ChannelDeltas> | null
}

export const ChannelPerformanceTable = ({
  stats,
  isLoading,
  toggleSort,
  canEditChannelExpense,
  canEditSourceExpense,
  canDeleteExpense,
  shouldShowDelete,
  openDeleteConfirmation,
  editingExpense,
  setEditingExpense,
  expenseInputs,
  setExpenseInputs,
  saveExpense,
  expenseEditingRules,
  expandedChannels,
  toggleChannel,
  sourceFilters,
  updateSourceFilter,
  getFilteredSources,
  expandedInfluencers,
  toggleInfluencerRow,
  influencers,
  openCreateInfluencerDialog,
  openAddCodeDialog,
  setInfluencerActionPrompt,
  onMetadataBadgeClick,
  channelDeltas,
  sourceDeltas,
}: ChannelPerformanceTableProps) => {
  const { formatCostPerOrderDisplay, formatRoiDisplay } = useAttributionFormatters()


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardChannelTable")

  if (isLoading) {
    return (
      <Table mobileLayout="scroll">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]" />
            <TableHead><span key={tUi("channelSource")}>{tUi("channelSource")}</span></TableHead>
            <TableHead><span key={tUi("orders")}>{tUi("orders")}</span></TableHead>
            <TableHead><span key={tUi("revenue")}>{tUi("revenue")}</span></TableHead>
            {channelDeltas && <TableHead><span key={tUi("rev")}>{tUi("rev")}</span></TableHead>}
            <TableHead><span key={tUi("avgOrderValue")}>{tUi("avgOrderValue")}</span></TableHead>
            <TableHead><span key={tUi("expense")}>{tUi("expense")}</span></TableHead>
            <TableHead><span key={tUi("costOrder")}>{tUi("costOrder")}</span></TableHead>
            <TableHead><span key={"ROI"}>{"ROI"}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }, (_, i) => (
            <TableRow key={`skeleton-${i}`}>
              <TableCell className="py-2"><Skeleton className="h-5 w-5" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-32" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-12" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-20" /></TableCell>
              {channelDeltas && <TableCell className="py-2"><Skeleton className="h-5 w-14" /></TableCell>}
              <TableCell className="py-2"><Skeleton className="h-5 w-20" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-16" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-16" /></TableCell>
              <TableCell className="py-2"><Skeleton className="h-5 w-14" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  const maxRevenue = Math.max(...stats.map((s) => s.revenue), 1)
  const maxOrders = Math.max(...stats.map((s) => s.orders), 1)

  const renderRevenueDeltaCell = (
    deltaMap: Map<string, ChannelDeltas> | null | undefined,
    key: string,
  ) => {
    if (!channelDeltas) return null
    const delta = deltaMap?.get(key)?.revenue
    return (
      <TableCell className="py-2">
        {delta ? <DeltaBadge delta={delta} /> : null}
      </TableCell>
    )
  }

  const renderMetadataBadges = (
    source: SourceDetail,
    keyPrefix: string,
    baseContext: Omit<MetadataBadgeContext, "badgeType">,
  ) => {
    const badges: React.ReactNode[] = []

    if (source.isArchived) {
      badges.push(
        <Badge key={`${keyPrefix}-status`} variant="secondary" className="bg-muted text-muted-foreground">
          <span key={tUi("archived")}>{tUi("archived")}</span></Badge>,
      )
    }

    if ((source.utmMetrics && source.utmMetrics.length > 0) || (source.referrerUrls && source.referrerUrls.length > 0)) {
      const combinedMetrics: string[] = []
      if (source.utmMetrics && source.utmMetrics.length > 0) {
        combinedMetrics.push(...Array.from(new Set(source.utmMetrics)))
      }
      if (source.referrerUrls && source.referrerUrls.length > 0) {
        combinedMetrics.push(...Array.from(new Set(source.referrerUrls)))
      }
      const utmContent = combinedMetrics.join("\n\n")
      const handleActivation = (event?: React.KeyboardEvent<HTMLDivElement>) => {
        if (event) {
          const isActivationKey = event.key === "Enter" || event.key === " "
          if (!isActivationKey) return
          event.preventDefault()
        }
        onMetadataBadgeClick({
          ...baseContext,
          badgeType: "utm",
        })
      }
      badges.push(
        <Tooltip key={`${keyPrefix}-utm`}>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() =>
                onMetadataBadgeClick({
                  ...baseContext,
                  badgeType: "utm",
                })
              }
              onKeyDown={(event) => handleActivation(event)}
            >
              <span key={"UTM"}>{"UTM"}</span></Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-md whitespace-pre-line text-xs">
            {utmContent}
          </TooltipContent>
        </Tooltip>,
      )
    }


    // don't show discount badge for influencer codes
    if (source.influencerCode) {
      return badges
    }

    const discountEntries: string[] = []
    if (source.discountCodes && source.discountCodes.length > 0) {
      discountEntries.push(
        ...source.discountCodes.map((item) => `${item.code}${item.count !== undefined ? ` (${item.count})` : ""}`),
      )
    }

    if (source.actualCode && !discountEntries.includes(source.actualCode)) {
      discountEntries.push(source.actualCode)
    }

    if (discountEntries.length > 0) {
      const lines = Array.from(new Set(discountEntries))
      const handleActivation = (event?: React.KeyboardEvent<HTMLDivElement>) => {
        if (event) {
          const isActivationKey = event.key === "Enter" || event.key === " "
          if (!isActivationKey) return
          event.preventDefault()
        }
        onMetadataBadgeClick({
          ...baseContext,
          badgeType: "discount",
        })
      }
      badges.push(
        <Tooltip key={`${keyPrefix}-discount`}>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() =>
                onMetadataBadgeClick({
                  ...baseContext,
                  badgeType: "discount",
                })
              }
              onKeyDown={(event) => handleActivation(event)}
            >
              <span key={tUi("discount")}>{tUi("discount")}</span></Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-md whitespace-pre-line text-xs">
            {lines.join("\n")}
          </TooltipContent>
        </Tooltip>,
      )
    }

    return badges
  }

  const renderExpenseCell = (
    channelSlug: string,
    channelName: string,
    sourceName: string | undefined,
    expenseValue: number,
    editRules: { canEdit: boolean; reason: string },
  ) => {
    const key = sourceName ? `${channelSlug}:${sourceName}` : channelSlug
    const isEditing =
      editingExpense?.channelSlug === channelSlug && (sourceName ? editingExpense?.source === sourceName : !editingExpense?.source)

    return (
      <ExpenseCell
        displayValue={expenseValue}
        isEditing={isEditing}
        currentValue={expenseInputs[key] ?? expenseValue}
        onChange={(value) =>
          setExpenseInputs((prev) => ({
            ...prev,
            [key]: value,
          }))
        }
        onSave={() => saveExpense(channelSlug, channelName, sourceName)}
        onCancel={() => setEditingExpense(null)}
        canEdit={editRules.canEdit}
        viewButtonDisabledReason={editRules.canEdit ? undefined : editRules.reason}
        isMultiMonth={expenseEditingRules.isMultiMonth}
        onEnterEdit={() => {
          if (editRules.canEdit) {
            setEditingExpense({ channelSlug, source: sourceName })
            setExpenseInputs((prev) => ({
              ...prev,
              [key]: expenseValue,
            }))
          }
        }}
        deleteButton={
          shouldShowDelete(channelSlug, sourceName)
            ? {
                visible: true,
                canDelete: canDeleteExpense(channelSlug, sourceName).canDelete,
                reason: canDeleteExpense(channelSlug, sourceName).reason,
                onDelete: () => openDeleteConfirmation(channelSlug, channelName, sourceName),
              }
            : undefined
        }
      />
    )
  }

  return (
    <TooltipProvider>
        <Table mobileLayout="scroll">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]" />
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort("channel")} className="h-8 px-2">
                <span><span key={tUi("channelSource")}>{tUi("channelSource")}</span></span>
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort("orders")} className="h-8 px-2">
                <span><span key={tUi("orders")}>{tUi("orders")}</span></span>
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort("revenue")} className="h-8 px-2">
                <span><span key={tUi("revenue")}>{tUi("revenue")}</span></span>
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            {channelDeltas && <TableHead><span key={tUi("rev")}>{tUi("rev")}</span></TableHead>}
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort("aov")} className="h-8 px-2">
                <span><span key={tUi("avgOrderValue")}>{tUi("avgOrderValue")}</span></span>
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <span><span key={tUi("expense")}>{tUi("expense")}</span></span><sup>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px]">
                    <p className="text-xs"><span key={tUi("editableForSingleMonthPeriodsOnlySetAt")}>{tUi("editableForSingleMonthPeriodsOnlySetAt")}</span></p>
                  </TooltipContent>
                </Tooltip>
              </sup>
            </TableHead>
            <TableHead><span key={tUi("costOrder")}>{tUi("costOrder")}</span></TableHead>
            <TableHead>
              <Button variant="ghost" onClick={() => toggleSort("roi")} className="h-8 px-2">
                <span><span key={"ROI"}>{"ROI"}</span></span>
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stats.map((stat) => {
            const channelKey = stat.channelSlug.toLowerCase()
            const deltaKey = stat.channelSlug
            const channelEditRules = canEditChannelExpense(stat.channelSlug, {
              allowChannelLevelExpense: stat.channelMeta?.allowChannelLevelExpense,
            })
            const channelDeleteRules = canDeleteExpense(stat.channelSlug, undefined)
            const showChannelDelete = shouldShowDelete(stat.channelSlug, undefined)
            const filteredSources = getFilteredSources(stat.channelSlug, stat.sources)

            return (
              <React.Fragment key={stat.channelSlug}>
                <TableRow
                  className={cn("font-medium", stat.channelColor && "border-l-4")}
                  style={stat.channelColor ? { borderLeftColor: stat.channelColor } : undefined}
                >
                  <TableCell className="py-2">
                    {stat.sources.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleChannel(channelKey)}
                        aria-label={expandedChannels.has(channelKey) ? tUi("collapseChannel") : tUi("expandChannel")}
                      >
                        {expandedChannels.has(channelKey) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="py-2 font-semibold">
                    <div className="flex items-center gap-2">
                      {stat.channel}
                      {stat.channelSlug === "influencers" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={openCreateInfluencerDialog}
                          aria-label={tUi("addInfluencer")}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <InlineBar value={stat.orders} max={maxOrders} color="hsl(var(--chart-2))">
                      {stat.orders}
                    </InlineBar>
                  </TableCell>
                  <TableCell className="py-2">
                    <InlineBar value={stat.revenue} max={maxRevenue} color="hsl(var(--chart-1))">
                      {formatCurrency(stat.revenue)}
                    </InlineBar>
                  </TableCell>
                  {renderRevenueDeltaCell(channelDeltas, deltaKey)}
                  <TableCell className="py-2">{formatCurrency(stat.averageOrderValue)}</TableCell>
                  <TableCell className="py-2">
                    <ExpenseCell
                      displayValue={stat.expense ?? 0}
                      isEditing={editingExpense?.channelSlug === stat.channelSlug && !editingExpense?.source}
                      currentValue={expenseInputs[stat.channelSlug] ?? stat.channelExpense ?? 0}
                      onChange={(value) =>
                        setExpenseInputs((prev) => ({
                          ...prev,
                          [stat.channelSlug]: value,
                        }))
                      }
                      onSave={() => saveExpense(stat.channelSlug, stat.channel)}
                      onCancel={() => setEditingExpense(null)}
                      canEdit={channelEditRules.canEdit}
                      viewButtonDisabledReason={channelEditRules.reason}
                      isMultiMonth={expenseEditingRules.isMultiMonth}
                      onEnterEdit={() => {
                        if (channelEditRules.canEdit) {
                          setEditingExpense({ channelSlug: stat.channelSlug })
                          setExpenseInputs((prev) => ({
                            ...prev,
                            [stat.channelSlug]: stat.channelExpense ?? 0,
                          }))
                        }
                      }}
                      deleteButton={
                        showChannelDelete
                          ? {
                              visible: true,
                              canDelete: channelDeleteRules.canDelete,
                              reason: channelDeleteRules.reason,
                              onDelete: () => openDeleteConfirmation(stat.channelSlug, stat.channel, undefined),
                            }
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell className="py-2">{formatCostPerOrderDisplay(stat.costPerOrder, stat.expense)}</TableCell>
                  <TableCell className="py-2">
                    {isExpenseEmpty(stat.expense) ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Badge
                        variant={isRoiPositive(stat.roi, stat.expense) ? "default" : "destructive"}
                        className={cn(isRoiPositive(stat.roi, stat.expense) ? "bg-green-100 text-green-800" : "")}
                      >
                        {formatRoiDisplay(stat.roi, stat.expense)}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>

                {expandedChannels.has(channelKey) && (
                  <>
                    {stat.sources.length > 0 && (
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableCell />
                        <TableCell colSpan={channelDeltas ? 8 : 7} className="py-2">
                          <div className="flex items-center gap-2 pl-8">
                            <Search className="h-4 w-4 text-muted-foreground" />
                            <Input
                              type="text"
                              placeholder={tUi("filterSourcesByName")}
                              value={sourceFilters[channelKey] || ""}
                              onChange={(event) => updateSourceFilter(stat.channelSlug, event.target.value)}
                              className="h-8 max-w-xs text-sm"
                            />
                            {sourceFilters[channelKey] && (
                              <span className="text-xs text-muted-foreground"><TranslationText>{tUi.rich("value0OfValue1Sources", { value0: String(filteredSources.length), value1: String(stat.sources.length) })}</TranslationText></span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredSources.map((source) => {
                      const baseSourceEditRules = canEditSourceExpense(stat.channelSlug, source.sourceName)
                      const filteredKey = source.influencerId || source.sourceKey
                      const influencerRecord =
                        stat.channelSlug === "influencers"
                          ? influencers.find((influencer) => influencer.id === filteredKey) ?? null
                          : null

                    if (stat.channelSlug === "influencers" && influencerRecord) {
                      const influencerKey = `${stat.channelSlug}:${influencerRecord.id}`
                      const isInfluencerExpanded = expandedInfluencers.has(influencerKey)
                      const isInfluencerArchived = Boolean(influencerRecord.archived_at)
                      const childHasExpense = (source.children || []).some(
                        (child) => (child.selfExpense ?? child.expense ?? 0) > 0,
                      )
                      const parentSelfExpense = source.selfExpense ?? 0
                      const influencerEditRules = childHasExpense
                        ? {
                            canEdit: false,
                            reason: "Expenses exist for discount codes under this influencer",
                          }
                        : baseSourceEditRules
                      const influencerBadges = renderMetadataBadges(source, `${influencerKey}-meta`, {
                        channelName: stat.channel,
                        channelSlug: stat.channelSlug,
                        scope: "influencer",
                        sourceName: source.sourceName,
                        sourceLabel: source.sourceLabel,
                        influencerId: source.influencerId ?? influencerRecord.id,
                        influencerName: influencerRecord.name,
                        discountCode: null,
                        orderIds: source.orderIds ?? [],
                      })

                      return (
                        <React.Fragment key={`influencer-row-${influencerKey}`}>
                          <TableRow
                            className={cn(
                              "bg-muted/30 hover:bg-muted/40 transition-colors",
                              influencerRecord.archived_at && "opacity-70",
                            )}
                          >
                            <TableCell className="py-2">
                              {(source.children || []).length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => toggleInfluencerRow(influencerKey)}
                                  aria-label={isInfluencerExpanded ? tUi("collapseInfluencer") : tUi("expandInfluencer")}
                                >
                                  {isInfluencerExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="py-2 pl-8">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={cn(
                                    "font-medium",
                                    isInfluencerArchived && "line-through text-muted-foreground",
                                  )}
                                >
                                  {influencerRecord.name}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={isInfluencerArchived}
                                        onClick={() => openAddCodeDialog(influencerRecord)}
                                        className="h-6 w-6 p-0"
                                        aria-label={tUi("addDiscountCode")}
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <span key={isInfluencerArchived
                                        ? tUi("archivedInfluencersCannotReceiveNewCodes")
                                        : tUi("addDiscountCode")}>{isInfluencerArchived
                                        ? tUi("archivedInfluencersCannotReceiveNewCodes")
                                        : tUi("addDiscountCode")}</span>
                                    </TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          setInfluencerActionPrompt({
                                            scope: "influencer",
                                            influencerId: influencerRecord.id,
                                            influencerName: influencerRecord.name,
                                            isInfluencerArchived,
                                          })
                                        }
                                        className="h-6 w-6 p-0"
                                        aria-label={tUi("archiveOrDeleteInfluencer")}
                                      >
                                        <Trash2 className="h-3 w-3 text-destructive" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <span key={isInfluencerArchived ? tUi("deleteInfluencer") : tUi("archiveOrDeleteInfluencer")}>{isInfluencerArchived ? tUi("deleteInfluencer") : tUi("archiveOrDeleteInfluencer")}</span>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                {influencerBadges.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-1">{influencerBadges}</div>
                                )}
                              </div>
                            </TableCell>
                              <TableCell className="py-2">{source.orders}</TableCell>
                              <TableCell className="py-2">{formatCurrency(source.revenue)}</TableCell>
                              {renderRevenueDeltaCell(sourceDeltas, `${deltaKey}:${source.sourceName}`)}
                              <TableCell className="py-2">{formatCurrency(source.averageOrderValue)}</TableCell>
                              <TableCell className="py-2">
                                {renderExpenseCell(
                                  stat.channelSlug,
                                  stat.channel,
                                  source.sourceName,
                                  source.expense ?? 0,
                                  influencerEditRules,
                                )}
                              </TableCell>
                              <TableCell className="py-2 text-sm">{formatCostPerOrderDisplay(source.costPerOrder, source.expense)}</TableCell>
                              <TableCell className="py-2">
                                {isExpenseEmpty(source.expense) ? (
                                  <span className="text-muted-foreground text-xs">—</span>
                                ) : (
                                  <Badge
                                    variant={isRoiPositive(source.roi, source.expense) ? "default" : "destructive"}
                                    className={cn(
                                      "text-xs",
                                      isRoiPositive(source.roi, source.expense) ? "bg-green-100 text-green-800" : "",
                                    )}
                                  >
                                    {formatRoiDisplay(source.roi, source.expense)}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                            {isInfluencerExpanded &&
                              (source.children || []).map((child) => {
                                const childEditRules = canEditSourceExpense(stat.channelSlug, child.sourceName)
                                const childBadges = renderMetadataBadges(child, `${influencerKey}-${child.sourceKey}`, {
                                  channelName: stat.channel,
                                  channelSlug: stat.channelSlug,
                                  scope: "code",
                                  sourceName: child.sourceName,
                                  sourceLabel: child.sourceLabel,
                                  influencerId: source.influencerId ?? influencerRecord.id,
                                  influencerName: influencerRecord.name,
                                  discountCode: child.actualCode ?? child.sourceName ?? null,
                                  orderIds: child.orderIds ?? [],
                                })
                                const isCodeArchived = Boolean(child.isArchived)
                                const effectiveChildEditRules = parentSelfExpense > 0
                                  ? {
                                      canEdit: false,
                                      reason: "Expense recorded at influencer level",
                                    }
                                  : childEditRules

                                return (
                                  <TableRow key={`child-${child.sourceKey}`} className="bg-muted/10 hover:bg-muted/20 transition-colors">
                                    <TableCell className="py-2" />
                                    <TableCell className="py-2 pl-16">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span
                                          className={cn(
                                            "font-medium",
                                            isCodeArchived && "line-through text-muted-foreground",
                                          )}
                                        >
                                          {child.sourceLabel}
                                        </span>
                                        {childBadges.length > 0 && (
                                          <div className="flex flex-wrap items-center gap-1">{childBadges}</div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  setInfluencerActionPrompt({
                                                    scope: "code",
                                                    influencerId: influencerRecord.id,
                                                    influencerName: influencerRecord.name,
                                                    code: child.sourceName,
                                                    isCodeArchived,
                                                  })
                                                }
                                                className="h-6 w-6 p-0"
                                                aria-label={
                                                  isCodeArchived ? tUi("deleteDiscountCode") : tUi("archiveOrDeleteDiscountCode")
                                                }
                                              >
                                                <Trash2 className="h-3 w-3 text-destructive" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <span key={isCodeArchived ? tUi("deleteDiscountCode") : tUi("archiveOrDeleteDiscountCode")}>{isCodeArchived ? tUi("deleteDiscountCode") : tUi("archiveOrDeleteDiscountCode")}</span>
                                            </TooltipContent>
                                          </Tooltip>
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">{child.orders}</TableCell>
                                    <TableCell className="py-2">{formatCurrency(child.revenue)}</TableCell>
                                    {renderRevenueDeltaCell(sourceDeltas, `${deltaKey}:${source.sourceName}:${child.sourceName}`)}
                                    <TableCell className="py-2">{formatCurrency(child.averageOrderValue)}</TableCell>
                                    <TableCell className="py-2">
                                      {renderExpenseCell(
                                        stat.channelSlug,
                                        stat.channel,
                                        child.sourceName,
                                        child.selfExpense ?? child.expense ?? 0,
                                        effectiveChildEditRules,
                                      )}
                                    </TableCell>
                                    <TableCell className="py-2 text-sm">{formatCostPerOrderDisplay(child.costPerOrder, child.expense)}</TableCell>
                                    <TableCell className="py-2">
                                      {isExpenseEmpty(child.expense) ? (
                                        <span className="text-muted-foreground text-xs">—</span>
                                      ) : (
                                        <Badge
                                          variant={isRoiPositive(child.roi, child.expense) ? "default" : "destructive"}
                                          className={cn(
                                            "text-xs",
                                            isRoiPositive(child.roi, child.expense) ? "bg-green-100 text-green-800" : "",
                                          )}
                                        >
                                          {formatRoiDisplay(child.roi, child.expense)}
                                        </Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                          </React.Fragment>
                        )
                      }

                      const sourceBadges = renderMetadataBadges(source, `${stat.channelSlug}-${source.sourceKey}`, {
                        channelName: stat.channel,
                        channelSlug: stat.channelSlug,
                        scope: "source",
                        sourceName: source.sourceName,
                        sourceLabel: source.sourceLabel,
                        influencerId: source.influencerId ?? null,
                        influencerName: source.influencerName ?? null,
                        discountCode: source.actualCode ?? source.discountCodeUsed ?? null,
                        orderIds: source.orderIds ?? [],
                      })

                      return (
                        <TableRow
                          key={`${stat.channelSlug}-${source.sourceKey}`}
                          className="hover:bg-muted/40 transition-colors"
                        >
                          <TableCell className="py-2" />
                          <TableCell className="py-2 pl-8">
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">{source.sourceName}</span>
                              {sourceBadges.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">{sourceBadges}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">{source.orders}</TableCell>
                          <TableCell className="py-2">{formatCurrency(source.revenue)}</TableCell>
                          {renderRevenueDeltaCell(sourceDeltas, `${deltaKey}:${source.sourceName}`)}
                          <TableCell className="py-2">{formatCurrency(source.averageOrderValue)}</TableCell>
                          <TableCell className="py-2">
                            {renderExpenseCell(stat.channelSlug, stat.channel, source.sourceName, source.expense ?? 0, baseSourceEditRules)}
                          </TableCell>
                          <TableCell className="py-2 text-sm">{formatCostPerOrderDisplay(source.costPerOrder, source.expense)}</TableCell>
                          <TableCell className="py-2">
                            {isExpenseEmpty(source.expense) ? (
                              <span className="text-muted-foreground text-xs">—</span>
                            ) : (
                              <Badge
                                variant={isRoiPositive(source.roi, source.expense) ? "default" : "destructive"}
                                className={cn(
                                  "text-xs",
                                  isRoiPositive(source.roi, source.expense) ? "bg-green-100 text-green-800" : "",
                                )}
                              >
                                {formatRoiDisplay(source.roi, source.expense)}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </>
                )}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  )
}
